import React, { useEffect, useRef, useState } from 'react';
import { Player } from '@remotion/player';
import { ConversationAnimation } from './ConversationAnimation';
import { HowItWorksAnimation } from './HowItWorksAnimation';

/**
 * useDelayedPlay - Handles animation playback with proper end state
 *
 * 1. Starts at frame 0 (initial state visible)
 * 2. After delay, plays animation from start
 * 3. Polls frame position and pauses at final frame BEFORE player resets
 */
const useDelayedPlay = (delay = 5000, finalFrame) => {
  const ref = useRef(null);
  const playerRef = useRef(null);
  const [triggered, setTriggered] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const pausedAtEndRef = useRef(false);

  // Trigger playback after delay when scrolled into view
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !triggered) {
          setTriggered(true);
          setTimeout(() => {
            const player = playerRef.current;
            if (player) {
              player.seekTo(0);
              player.play();
              setIsPlaying(true);
            }
          }, delay);
        }
      },
      { threshold: 0.5 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [triggered, delay]);

  // Poll frame position while playing, pause at final frame
  useEffect(() => {
    if (!isPlaying) return;

    const checkFrame = () => {
      const player = playerRef.current;
      if (!player || pausedAtEndRef.current) return;

      const currentFrame = player.getCurrentFrame();
      // Pause 2 frames before the end to prevent reset
      if (currentFrame >= finalFrame - 2) {
        pausedAtEndRef.current = true;
        player.pause();
        player.seekTo(finalFrame);
        setIsPlaying(false);
      }
    };

    const interval = setInterval(checkFrame, 50); // Check every 50ms
    return () => clearInterval(interval);
  }, [isPlaying, finalFrame]);

  return { ref, playerRef };
};

/**
 * Animated "How It Works" section
 *
 * Timeline:
 * - Frame 0: Only Step 1 visible (initial view)
 * - Frames 0-60: Arrow appears
 * - Frames 60-90: Step 2 fades in
 * - Frames 90-210: Rows fill in
 * - Frames 210-270: NON-VIABLE + LEASED
 * - Frame 270+: Frozen forever
 */
export const AnimatedHowItWorks = () => {
  const { ref, playerRef } = useDelayedPlay(5000, 299);
  return (
    <div ref={ref} className="animated-how-it-works">
      <Player
        ref={playerRef}
        component={HowItWorksAnimation}
        durationInFrames={300}
        compositionWidth={1420}
        compositionHeight={1100}
        fps={30}
        initialFrame={0}
        style={{ width: '100%', maxWidth: 1420, aspectRatio: '71 / 55', borderRadius: 16 }}
        loop={false}
        autoPlay={false}
        controls={false}
        acknowledgeRemotionLicense
      />
    </div>
  );
};

/**
 * Animated conversation demo
 * Shows: AI outreach → Broker reply → Data extraction → Thank you → Complete
 */
export const AnimatedConversation = () => {
  const { ref, playerRef } = useDelayedPlay(5000, 1259);
  return (
    <div ref={ref} className="animated-conversation">
      <Player
        ref={playerRef}
        component={ConversationAnimation}
        durationInFrames={1260}
        compositionWidth={1100}
        compositionHeight={950}
        fps={30}
        initialFrame={0}
        style={{ width: '100%', maxWidth: 1100, aspectRatio: '22 / 19' }}
        loop={false}
        autoPlay={false}
        controls={false}
        acknowledgeRemotionLicense
      />
    </div>
  );
};

const AnimatedSections = {
  AnimatedHowItWorks,
  AnimatedConversation,
};

export default AnimatedSections;
